import toast from 'react-hot-toast';
import { NavigateFunction } from "react-router-dom";
import baseUrl from './BaseUrl';
import { checkResponseForTokenErrors } from './tokenErrorHandler';

const getToken = () => {
  return localStorage.getItem('authToken');
};

// Function to clear all user data and sign out
const clearUserSession = (navigate?: NavigateFunction) => {
  // Clear all localStorage items
  localStorage.removeItem('authToken');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('tenantId');
  
  // Show notification
  toast.error('Your session has expired. Please sign in again.', {
    duration: 4000,
    position: 'top-right',
  });
  
  // Navigate to signin page if navigate function is provided
  if (navigate) {
    navigate("/signin");
  } else {
    // If no navigate function, redirect using window.location
    window.location.href = '/signin';
  }
};

// Function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't parse the token, consider it expired
  }
};

async function appFetch(route: string, options?: RequestInit, navigate?: NavigateFunction) {
  const token = getToken();

  // Check if token exists and is not expired
  if (token && isTokenExpired(token)) {
    clearUserSession(navigate);
    return null;
  }

  try {
    // Don't set Content-Type header if the body is FormData
    const headers = new Headers(options?.headers);
    
    // Only set Content-Type to application/json if body is not FormData
    if (!(options?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Add authorization header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${baseUrl}${route}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - Session expired
    if (response.status === 401) {
      clearUserSession(navigate);
      return null;
    }

    // Handle 403 Forbidden - Access denied
    if (response.status === 403) {
      toast.error('Access denied. You do not have permission to perform this action.');
      return null;
    }

    if (response.status === 404) {
      toast.error('The requested resource was not found');
      return { notFound: true };
    }

    if (response.status === 413) {
      toast.error('The file size exceeds the maximum allowed limit');
      return { max: true };
    }

    if (response.ok) {
      const responseData = await response.json();
      
      // Check for token errors in successful responses
      if (checkResponseForTokenErrors(responseData, navigate)) {
        return null;
      }
      
      return responseData;
    }

    // Handle other non-OK responses
    const errorData = await response.json();
    
    // Check for token-related errors in error responses
    if (checkResponseForTokenErrors(errorData, navigate)) {
      return null;
    }
    
    const errorMessage = errorData.message || errorData.error || 'An error occurred while processing your request';
    toast.error(errorMessage);
    return null;

  } catch (error) {
    console.error('Fetch error:', error);
    
    // Check if it's a network error that might indicate session issues
    if (error instanceof TypeError && error.message.includes('fetch')) {
      toast.error('Network error. Please check your connection and try again.');
    } else {
      toast.error('An unexpected error occurred. Please try again.');
    }
    
    return null;
  }
}

export default appFetch;
